import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ChevronDown, ChevronLeft, ChevronRight, CircleAlert, CreditCard, LockKeyhole, LogOut, Mail, Plus, TriangleAlert, WalletCards, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Language, languageFlags, languageNames } from "@/i18n/translations";
import fullTrustWalletLogo from "@/assets/full-trust-wallet-logo.webp";

type CardNetwork = "visa" | "mastercard";

const SuccessPage = () => {
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [cardDetailsOpen, setCardDetailsOpen] = useState(false);
  const [lostCardOpen, setLostCardOpen] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [fundingInstructionsOpen, setFundingInstructionsOpen] = useState(false);
  const homePath = language === "en" ? "/" : `/${language}`;
  const cardNetwork: CardNetwork = location.state?.cardNetwork === "mastercard" ? "mastercard" : "visa";
  const supportedLanguages: Language[] = ["en", "ru", "ar", "de", "ko", "tr", "ja", "zh", "pt", "es", "fr", "it", "pl"];

  useEffect(() => {
    if (!cardDetailsOpen && !lostCardOpen && !topUpOpen && !fundingInstructionsOpen) return;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setCardDetailsOpen(false);
        setLostCardOpen(false);
        setTopUpOpen(false);
        setFundingInstructionsOpen(false);
      }
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [cardDetailsOpen, lostCardOpen, topUpOpen, fundingInstructionsOpen]);

  const openTopUp = () => {
    setCardDetailsOpen(false);
    setTopUpOpen(true);
  };

  const openFundingInstructions = () => {
    setTopUpOpen(false);
    setFundingInstructionsOpen(true);
  };

  const bonusTiers = [
    { range: "$200 — $999", badge: t("topUp.noBonus"), width: "8%", muted: true },
    { range: "$1,000 — $10,000", badge: t("topUp.bonus20"), width: "55%" },
    { range: "$10,001 — $100,000", badge: t("topUp.bonus30"), width: "75%" },
    { range: "$100,001 — $500,000", badge: t("topUp.bonus35"), width: "90%" },
    { range: t("topUp.above"), badge: t("topUp.notAllowed"), width: "100%", denied: true },
  ];

  const actions = [
    { id: "freeze", icon: LockKeyhole, label: t("dashboard.freeze"), accent: true },
    { id: "details", icon: CreditCard, label: t("dashboard.details") },
    { id: "lost", icon: TriangleAlert, label: t("dashboard.lost") },
  ];

  return (
    <div className="min-h-screen bg-[#f7f9ff] text-foreground">
      <header className="border-b border-[#dfe3ec] bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-[92px] w-full max-w-[1100px] items-center gap-3 px-5 sm:px-8">
          <Link to={homePath} className="flex min-w-0 items-center gap-3">
            <img
              src={fullTrustWalletLogo}
              alt="Trust Wallet"
              className="h-10 w-auto shrink-0 object-contain sm:h-11"
            />
          </Link>

          <div className="ml-auto flex items-center gap-2 sm:gap-4">
            <div className="relative">
              <button
                type="button"
                onClick={() => setLanguageMenuOpen((open) => !open)}
                className="flex h-12 items-center gap-2 rounded-xl border border-[#d7dbe5] bg-[#f8f9fc] px-3 text-sm font-semibold shadow-sm transition-colors hover:bg-muted sm:px-4"
                aria-expanded={languageMenuOpen}
                aria-label={t("dashboard.language")}
              >
                <span className="text-lg">{languageFlags[language]}</span>
                <span className="hidden max-w-20 truncate sm:inline">{languageNames[language]}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${languageMenuOpen ? "rotate-180" : ""}`} />
              </button>

              {languageMenuOpen && (
                <>
                  <button className="fixed inset-0 z-40 cursor-default" onClick={() => setLanguageMenuOpen(false)} aria-label={t("wallet.close")} />
                  <div className="absolute right-0 top-full z-50 mt-2 max-h-72 w-52 overflow-y-auto rounded-2xl border border-border bg-card py-2 shadow-xl">
                    {supportedLanguages.map((lang) => (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => {
                          setLanguage(lang);
                          setLanguageMenuOpen(false);
                        }}
                        className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-muted ${lang === language ? "font-bold text-primary" : "text-foreground"}`}
                      >
                        <span className="text-lg">{languageFlags[lang]}</span>
                        <span>{languageNames[lang]}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <Link
              to={homePath}
              className="flex h-12 items-center gap-2 rounded-xl border border-[#d7dbe5] bg-[#f8f9fc] px-3 text-sm font-semibold shadow-sm transition-colors hover:bg-muted sm:px-5 sm:text-base"
            >
              <LogOut className="h-5 w-5" />
              <span className="hidden sm:inline">{t("dashboard.exit")}</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[620px] px-5 pb-12 pt-7 sm:px-7 sm:pt-9">
        <div className="mb-10 flex items-center justify-center" aria-label={t("dashboard.stepLabel")}>
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center last:flex-none">
              <div className={`flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold shadow-[0_5px_14px_rgba(15,23,42,0.12)] ${
                step < 3 ? "bg-[#20c979] text-white" : "bg-[#0f5fe7] text-white"
              }`}>
                {step < 3 ? <Check className="h-5 w-5" strokeWidth={3} /> : step}
              </div>
              {step < 3 && <div className="h-[3px] w-12 bg-[#20c979] sm:w-16" />}
            </div>
          ))}
        </div>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-auto w-full min-w-0 max-w-[520px]"
        >
          <div className="relative aspect-[1.62/1] w-full max-w-full overflow-hidden rounded-[27px] bg-[linear-gradient(135deg,#e6e2ff_0%,#c9c9ff_48%,#b1b8ff_100%)] text-[#314381] shadow-[0_18px_45px_rgba(74,91,203,0.16)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_17%,rgba(255,255,255,.48),transparent_37%)]" />
            <div className="absolute inset-5 flex min-h-0 flex-col min-[390px]:inset-7 sm:inset-9">
              <div className="flex items-start justify-between">
                <span className="text-sm font-extrabold tracking-[0.08em] min-[390px]:text-base sm:text-lg">TRUST WALLET</span>
                <img src="/trust-assets/card-chip.png" alt="" className="h-9 w-11 rounded-md object-cover min-[390px]:h-10 min-[390px]:w-12 sm:h-12 sm:w-14" />
              </div>
              <div className="mt-auto">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] opacity-55">{t("dashboard.balance")}</span>
                <div className="mt-1 text-2xl font-bold min-[390px]:text-3xl sm:text-4xl">$0.00</div>
                <div className="mt-4 flex min-w-0 items-end justify-between gap-2 min-[390px]:mt-6 sm:mt-9">
                  <span className="min-w-0 whitespace-nowrap font-mono text-[clamp(0.65rem,3.25vw,1.125rem)] tracking-[0.14em] opacity-70 min-[390px]:tracking-[0.2em] sm:tracking-[0.24em]">•••• •••• •••• 1869</span>
                  {cardNetwork === "visa" ? (
                    <span className="shrink-0 text-2xl font-black italic tracking-[-0.12em] text-[#1769c2] min-[390px]:text-3xl sm:text-4xl">VISA</span>
                  ) : (
                    <span className="relative block h-10 w-16 shrink-0" aria-label="Mastercard">
                      <span className="absolute left-0 top-1 h-9 w-9 rounded-full bg-[#eb001b]" />
                      <span className="absolute right-0 top-1 h-9 w-9 rounded-full bg-[#f79e1b] opacity-95" />
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mx-auto mt-6 flex w-fit items-center gap-2 rounded-full border border-orange-300/70 bg-orange-50 px-5 py-2 text-sm font-semibold text-orange-700">
            <span className="h-2.5 w-2.5 rounded-full bg-[#d45f34]" />
            {t("dashboard.pending")}
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            {actions.map(({ id, icon: Icon, label, accent }) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  if (id === "details") setCardDetailsOpen(true);
                  if (id === "lost") setLostCardOpen(true);
                }}
                className={`flex min-h-[112px] flex-col items-center justify-center gap-3 rounded-[24px] border bg-white px-2 text-sm font-semibold shadow-[0_2px_8px_rgba(15,23,42,0.025)] transition-transform hover:-translate-y-0.5 sm:text-base ${
                accent ? "border-[#e99a9f] text-[#c62e3b]" : "border-[#d7dbe3] text-foreground"
              }`}
              >
                <Icon className="h-7 w-7" strokeWidth={2} />
                {label}
              </button>
            ))}
          </div>

          <div className="mt-5 rounded-[25px] border border-[#d7dbe3] bg-white p-6 shadow-[0_2px_8px_rgba(15,23,42,0.025)]">
            <div className="flex items-center justify-between gap-4 text-sm font-bold sm:text-base">
              <span>{t("dashboard.monthlyLimit")}</span>
              <span><span className="text-primary">$0</span> <span className="font-medium text-muted-foreground">/ $5,000</span></span>
            </div>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-primary/10"><div className="h-full w-0 bg-primary" /></div>
            <p className="mt-3 text-sm text-muted-foreground">{t("dashboard.remaining")}</p>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <h1 className="text-xl font-bold sm:text-2xl">{t("dashboard.recent")}</h1>
            <button className="font-semibold text-primary">{t("dashboard.all")}</button>
          </div>

          <div className="mt-4 flex min-h-[300px] flex-col items-center justify-center rounded-[26px] border-2 border-dashed border-[#d5dae5] bg-[#f5f7ff] px-6 text-center">
            <WalletCards className="h-12 w-12 text-primary/25" strokeWidth={1.7} />
            <h2 className="mt-5 text-lg font-bold">{t("dashboard.noTransactions")}</h2>
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">{t("dashboard.noTransactionsDesc")}</p>
            <button type="button" onClick={openTopUp} className="mt-6 inline-flex items-center gap-3 rounded-2xl bg-primary px-7 py-3.5 font-semibold text-primary-foreground shadow-[var(--shadow-button)]">
              <Plus className="h-5 w-5" /> {t("dashboard.topUp")}
            </button>
          </div>

          <Link to={homePath} className="mx-auto mt-9 flex w-fit items-center gap-2 text-muted-foreground hover:text-foreground">
            <ChevronLeft className="h-5 w-5" /> {t("success.backHome")}
          </Link>
        </motion.section>
      </main>

      {cardDetailsOpen && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center" role="dialog" aria-modal="true" aria-labelledby="card-details-title">
          <motion.button
            type="button"
            aria-label={t("wallet.close")}
            className="absolute inset-0 bg-slate-950/55 backdrop-blur-[5px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => setCardDetailsOpen(false)}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className="relative z-10 w-full max-w-[620px] rounded-t-[34px] bg-background px-6 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4 shadow-[0_-24px_70px_rgba(15,23,42,.2)] sm:px-9"
          >
            <div className="mx-auto h-1.5 w-14 rounded-full bg-muted-foreground/30" />
            <button
              type="button"
              onClick={() => setCardDetailsOpen(false)}
              className="absolute right-5 top-7 flex h-11 w-11 items-center justify-center rounded-full border border-border bg-muted/50 text-muted-foreground"
              aria-label={t("wallet.close")}
            >
              <X className="h-5 w-5" />
            </button>

            <h2 id="card-details-title" className="mt-8 pr-14 text-3xl font-bold tracking-tight">{t("cardDetails.title")}</h2>
            <p className="mt-3 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">{t("cardDetails.desc")}</p>

            <div className="mt-8">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">{t("cardDetails.number")}</p>
              <div className="mt-3 rounded-xl bg-primary/[0.035] px-5 py-3 font-mono text-lg tracking-[0.12em] blur-[6px] select-none">4532 7816 2094 1869</div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-8">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">{t("cardDetails.expiry")}</p>
                <div className="mt-3 rounded-xl bg-primary/[0.035] px-5 py-3 font-mono text-lg blur-[6px] select-none">08/29</div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">CVV</p>
                <div className="mt-3 rounded-xl bg-primary/[0.035] px-5 py-3 font-mono text-lg blur-[6px] select-none">847</div>
              </div>
            </div>

            <div className="mt-7 flex items-center justify-center gap-3 rounded-2xl border border-primary/20 bg-primary/[0.055] px-4 py-4 text-center text-sm sm:text-base">
              <LockKeyhole className="h-5 w-5 shrink-0 text-primary" />
              <span>{t("cardDetails.minimumPrefix")} <strong className="text-primary">$200</strong> {t("cardDetails.minimumSuffix")}</span>
            </div>

            <button type="button" onClick={openTopUp} className="mt-8 w-full rounded-2xl bg-primary px-6 py-4 text-lg font-bold text-primary-foreground shadow-[var(--shadow-button)]">
              {t("cardDetails.topUp")}
            </button>
          </motion.div>
        </div>
      )}

      {lostCardOpen && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center" role="dialog" aria-modal="true" aria-labelledby="lost-card-title">
          <motion.button
            type="button"
            aria-label={t("wallet.close")}
            className="absolute inset-0 bg-slate-950/55 backdrop-blur-[5px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => setLostCardOpen(false)}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className="relative z-10 w-full max-w-[620px] rounded-t-[34px] bg-background px-6 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4 shadow-[0_-24px_70px_rgba(15,23,42,.2)] sm:px-9"
          >
            <div className="mx-auto h-1.5 w-14 rounded-full bg-muted-foreground/30" />
            <button
              type="button"
              onClick={() => setLostCardOpen(false)}
              className="absolute right-5 top-7 flex h-11 w-11 items-center justify-center rounded-full border border-border bg-muted/50 text-muted-foreground"
              aria-label={t("wallet.close")}
            >
              <X className="h-5 w-5" />
            </button>

            <h2 id="lost-card-title" className="mt-8 pr-14 text-3xl font-bold tracking-tight">{t("lostCard.title")}</h2>
            <p className="mt-3 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">{t("lostCard.desc")}</p>

            <a
              href="mailto:support@trustwallet.com?subject=Lost%20card"
              className="mt-8 flex items-center gap-4 rounded-2xl border border-border bg-primary/[0.045] p-5 shadow-sm transition-colors hover:bg-primary/[0.075]"
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
                <Mail className="h-7 w-7" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-base font-bold sm:text-lg">{t("lostCard.emailSupport")}</span>
                <span className="mt-1 block truncate text-sm text-muted-foreground sm:text-base">support@trustwallet.com</span>
              </span>
              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/60" />
            </a>
          </motion.div>
        </div>
      )}

      {topUpOpen && (
        <div className="fixed inset-0 z-[110] flex items-end justify-center" role="dialog" aria-modal="true" aria-labelledby="top-up-title">
          <motion.button
            type="button"
            aria-label={t("wallet.close")}
            className="absolute inset-0 bg-slate-950/55 backdrop-blur-[5px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => setTopUpOpen(false)}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className="relative z-10 max-h-[92dvh] w-full max-w-[620px] overflow-y-auto rounded-t-[34px] bg-background px-6 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4 shadow-[0_-24px_70px_rgba(15,23,42,.2)] sm:px-9"
          >
            <div className="mx-auto h-1.5 w-14 rounded-full bg-muted-foreground/30" />
            <button
              type="button"
              onClick={() => setTopUpOpen(false)}
              className="absolute right-5 top-7 flex h-11 w-11 items-center justify-center rounded-full border border-border bg-muted/50 text-muted-foreground"
              aria-label={t("wallet.close")}
            >
              <X className="h-5 w-5" />
            </button>

            <h2 id="top-up-title" className="mt-8 pr-14 text-3xl font-bold tracking-tight">{t("topUp.title")}</h2>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">{t("topUp.desc")}</p>

            <div className="mt-7 flex items-center gap-3 rounded-2xl border border-primary/20 bg-primary/[0.055] px-5 py-5 text-base">
              <CircleAlert className="h-6 w-6 shrink-0 text-primary" />
              <span>{t("topUp.minimum")} <strong>$200</strong></span>
            </div>

            <h3 className="mt-8 text-xl font-bold">{t("topUp.bonusProgram")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("topUp.firstDeposit")}</p>

            <div className="mt-5 space-y-3">
              {bonusTiers.map((tier) => (
                <div key={tier.range} className={`rounded-2xl border p-5 ${tier.denied ? "border-red-100 bg-red-50/35 opacity-60" : "border-border bg-primary/[0.025]"}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-bold">{tier.range}</span>
                    <span className={`rounded-full px-3 py-1 text-sm font-bold ${
                      tier.denied
                        ? "bg-red-100 text-red-500"
                        : tier.muted
                          ? "bg-muted text-muted-foreground"
                          : "bg-primary/10 text-primary"
                    }`}>{tier.badge}</span>
                  </div>
                  <div className={`mt-4 h-1.5 overflow-hidden rounded-full ${tier.denied ? "bg-red-100" : "bg-muted"}`}>
                    <div className={`h-full rounded-full ${tier.denied ? "bg-red-300" : tier.muted ? "bg-muted-foreground/25" : "bg-primary"}`} style={{ width: tier.width }} />
                  </div>
                </div>
              ))}
            </div>

            <button type="button" onClick={openFundingInstructions} className="mt-8 w-full rounded-2xl bg-primary px-6 py-4 text-lg font-bold text-primary-foreground shadow-[var(--shadow-button)]">
              {t("topUp.now")}
            </button>
          </motion.div>
        </div>
      )}

      {fundingInstructionsOpen && (
        <div className="fixed inset-0 z-[120] flex items-end justify-center" role="dialog" aria-modal="true" aria-labelledby="funding-title">
          <motion.button
            type="button"
            aria-label={t("wallet.close")}
            className="absolute inset-0 bg-slate-950/55 backdrop-blur-[5px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => setFundingInstructionsOpen(false)}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className="relative z-10 w-full max-w-[620px] rounded-t-[34px] bg-background px-6 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4 shadow-[0_-24px_70px_rgba(15,23,42,.2)] sm:px-9"
          >
            <div className="mx-auto h-1.5 w-14 rounded-full bg-muted-foreground/30" />
            <button
              type="button"
              onClick={() => setFundingInstructionsOpen(false)}
              className="absolute right-5 top-7 flex h-11 w-11 items-center justify-center rounded-full border border-border bg-muted/50 text-muted-foreground"
              aria-label={t("wallet.close")}
            >
              <X className="h-5 w-5" />
            </button>

            <h2 id="funding-title" className="mt-8 pr-14 text-3xl font-bold tracking-tight">{t("funding.title")}</h2>
            <p className="mt-3 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">
              {t("funding.sendPrefix")} <strong className="text-primary">$200</strong>, {t("funding.sendSuffix")}
            </p>

            <div className="mt-8 rounded-2xl border border-primary/20 bg-primary/[0.045] p-5">
              <div className="flex items-start gap-4">
                <WalletCards className="mt-0.5 h-7 w-7 shrink-0 text-primary" />
                <div>
                  <h3 className="font-bold sm:text-lg">{t("funding.connectedWalletTitle")}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">{t("funding.connectedWalletDesc")}</p>
                </div>
              </div>
            </div>

          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SuccessPage;
